import React, { useState, useCallback, useMemo, ChangeEvent } from 'react';
import { format, eachDayOfInterval, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Layers, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { SearchableMultiSelect } from '../ui/searchable-multi-select';
import {
  TemplateMode,
  HsmTemplate,
  CampaignCreateRequest
} from '../../types';
import {
  brazilianToISO,
  isoToBrazilian,
  isValidBrazilianDate,
  applyDateMask
} from '../../utils/date';

// Helper to get placeholder numbers from template body
function getTemplatePlaceholders(template: HsmTemplate): number[] {
  const bodyComponent = template.components?.find(c => c.type === 'BODY');
  const bodyText = bodyComponent?.text || '';
  const matches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
  const uniquePlaceholders = [...new Set(matches.map(m => parseInt(m.replace(/\{\{|\}\}/g, ''), 10)))];
  return uniquePlaceholders.sort((a, b) => a - b);
}

export interface SelectOption {
  value: number;
  label: string;
  description?: string;
}

export interface CreateCampaignDialogProps {
  open: boolean;
  approvedTemplates: HsmTemplate[];
  templatesLoading: boolean;
  batchOptions: SelectOption[];
  contactOptions: SelectOption[];
  batchesLoading: boolean;
  contactsLoading: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CampaignCreateRequest) => Promise<{ success: boolean; error?: string }>;
  getTemplateVariableMapping: (templateName: string) => Promise<{ success: boolean; variableMapping?: string[] | null; error?: string }>;
}

export function CreateCampaignDialog({
  open,
  approvedTemplates,
  templatesLoading,
  batchOptions,
  contactOptions,
  batchesLoading,
  contactsLoading,
  onOpenChange,
  onSubmit,
  getTemplateVariableMapping
}: CreateCampaignDialogProps) {
  // Form state
  const [campaignName, setCampaignName] = useState('');
  const [startDate, setStartDate] = useState(''); // ISO format
  const [startDateDisplay, setStartDateDisplay] = useState(''); // Brazilian format
  const [endDate, setEndDate] = useState(''); // ISO format
  const [endDateDisplay, setEndDateDisplay] = useState(''); // Brazilian format
  const [runTime, setRunTime] = useState('09:00');
  const [templateMode, setTemplateMode] = useState<TemplateMode>('SAME_FOR_ALL');
  const [defaultTemplateId, setDefaultTemplateId] = useState('');
  const [dayTemplates, setDayTemplates] = useState<Record<number, string>>({});
  const [defaultVariableMapping, setDefaultVariableMapping] = useState<string[]>([]);
  const [dayVariableMappings, setDayVariableMappings] = useState<Record<number, string[]>>({});
  const [defaultMappingAutoFetched, setDefaultMappingAutoFetched] = useState(false);
  const [dayMappingsAutoFetched, setDayMappingsAutoFetched] = useState<Record<number, boolean>>({});
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState<number[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate dates array for different per day mode
  const dateRange = useMemo(() => {
    if (!startDate || !endDate) return [];
    try {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      if (start > end) return [];
      return eachDayOfInterval({ start, end });
    } catch {
      return [];
    }
  }, [startDate, endDate]);

  // Helper function to initialize variable mapping with default value
  const initializeVariableMapping = useCallback((placeholders: number[]): string[] => {
    return placeholders.map(() => 'Nome');
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    setCampaignName('');
    setStartDate('');
    setStartDateDisplay('');
    setEndDate('');
    setEndDateDisplay('');
    setRunTime('09:00');
    setTemplateMode('SAME_FOR_ALL');
    setDefaultTemplateId('');
    setDayTemplates({});
    setDefaultVariableMapping([]);
    setDayVariableMappings({});
    setDefaultMappingAutoFetched(false);
    setDayMappingsAutoFetched({});
    setSelectedContactIds([]);
    setSelectedBatchIds([]);
    setError(null);
  }, []);

  // Handle start date input change
  const handleStartDateChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    setStartDateDisplay(masked);

    if (isValidBrazilianDate(masked)) {
      setStartDate(brazilianToISO(masked));
    } else {
      setStartDate('');
    }
  }, []);

  // Handle end date input change
  const handleEndDateChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    setEndDateDisplay(masked);

    if (isValidBrazilianDate(masked)) {
      setEndDate(brazilianToISO(masked));
    } else {
      setEndDate('');
    }
  }, []);

  // Update default variable mapping when template changes
  const handleDefaultTemplateChange = useCallback(async (templateId: string) => {
    setDefaultTemplateId(templateId);
    const template = approvedTemplates.find(t => t.id === templateId);
    if (template) {
      const placeholders = getTemplatePlaceholders(template);

      // Try to fetch stored mapping from backend
      const mappingResult = await getTemplateVariableMapping(template.name);
      if (mappingResult.success && mappingResult.variableMapping && mappingResult.variableMapping.length > 0) {
        setDefaultVariableMapping(mappingResult.variableMapping);
        setDefaultMappingAutoFetched(true);
      } else {
        setDefaultVariableMapping(initializeVariableMapping(placeholders));
        setDefaultMappingAutoFetched(false);
      }
    } else {
      setDefaultVariableMapping([]);
      setDefaultMappingAutoFetched(false);
    }
  }, [approvedTemplates, initializeVariableMapping, getTemplateVariableMapping]);

  // Update day template and its variable mapping
  const handleDayTemplateChange = useCallback(async (dayIndex: number, templateId: string) => {
    setDayTemplates(prev => ({
      ...prev,
      [dayIndex]: templateId
    }));
    const template = approvedTemplates.find(t => t.id === templateId);
    if (template) {
      const placeholders = getTemplatePlaceholders(template);

      // Try to fetch stored mapping from backend
      const mappingResult = await getTemplateVariableMapping(template.name);
      if (mappingResult.success && mappingResult.variableMapping && mappingResult.variableMapping.length > 0) {
        setDayVariableMappings(prev => ({
          ...prev,
          [dayIndex]: mappingResult.variableMapping!
        }));
        setDayMappingsAutoFetched(prev => ({
          ...prev,
          [dayIndex]: true
        }));
      } else {
        setDayVariableMappings(prev => ({
          ...prev,
          [dayIndex]: initializeVariableMapping(placeholders)
        }));
        setDayMappingsAutoFetched(prev => ({
          ...prev,
          [dayIndex]: false
        }));
      }
    } else {
      setDayVariableMappings(prev => {
        const newMappings = { ...prev };
        delete newMappings[dayIndex];
        return newMappings;
      });
      setDayMappingsAutoFetched(prev => {
        const newFlags = { ...prev };
        delete newFlags[dayIndex];
        return newFlags;
      });
    }
  }, [approvedTemplates, initializeVariableMapping, getTemplateVariableMapping]);

  // Handle create campaign
  const handleCreate = useCallback(async () => {
    if (!campaignName.trim()) {
      setError('Nome da campanha e obrigatorio');
      return;
    }
    if (!startDate || !endDate) {
      setError('Datas de inicio e fim sao obrigatorias');
      return;
    }
    if (templateMode === 'SAME_FOR_ALL' && !defaultTemplateId) {
      setError('Selecione um template');
      return;
    }
    if (templateMode === 'DIFFERENT_PER_DAY') {
      const missingDays = dateRange.some((_, index) => !dayTemplates[index]);
      if (missingDays) {
        setError('Selecione um template para cada dia');
        return;
      }
    }

    setCreating(true);
    setError(null);

    // Helper functions
    const getTemplateById = (templateId: string): HsmTemplate | undefined => {
      return approvedTemplates.find(t => t.id === templateId);
    };

    const getTemplateName = (templateId: string): string => {
      const template = getTemplateById(templateId);
      return template?.name || templateId;
    };

    const getTemplateLanguage = (templateId: string): string => {
      const template = getTemplateById(templateId);
      return template?.language || 'pt_BR';
    };

    const data: CampaignCreateRequest = {
      name: campaignName.trim(),
      startDate,
      endDate,
      runTime,
      templateMode,
      defaultTemplateId: templateMode === 'SAME_FOR_ALL' ? defaultTemplateId : undefined,
      defaultTemplateName: templateMode === 'SAME_FOR_ALL' ? getTemplateName(defaultTemplateId) : undefined,
      defaultLanguageCode: templateMode === 'SAME_FOR_ALL' ? getTemplateLanguage(defaultTemplateId) : undefined,
      defaultVariableMapping: templateMode === 'SAME_FOR_ALL' && defaultVariableMapping.length > 0
        ? defaultVariableMapping
        : undefined,
      dayTemplates: templateMode === 'DIFFERENT_PER_DAY'
        ? dateRange.map((date, index) => ({
            dayNumber: index + 1,
            date: format(date, 'yyyy-MM-dd'),
            templateId: dayTemplates[index],
            templateName: getTemplateName(dayTemplates[index]),
            languageCode: getTemplateLanguage(dayTemplates[index]),
            variableMapping: dayVariableMappings[index]?.length > 0
              ? JSON.stringify(dayVariableMappings[index])
              : undefined
          }))
        : undefined,
      contactIds: selectedContactIds.length > 0 ? selectedContactIds : undefined,
      batchIds: selectedBatchIds.length > 0 ? selectedBatchIds : undefined
    };

    const result = await onSubmit(data);
    setCreating(false);

    if (result.success) {
      onOpenChange(false);
      resetForm();
    } else {
      setError(result.error || 'Erro ao criar campanha');
    }
  }, [
    campaignName,
    startDate,
    endDate,
    runTime,
    templateMode,
    defaultTemplateId,
    dayTemplates,
    defaultVariableMapping,
    dayVariableMappings,
    dateRange,
    selectedContactIds,
    selectedBatchIds,
    approvedTemplates,
    onSubmit,
    onOpenChange,
    resetForm
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova Campanha</DialogTitle>
          <DialogDescription>
            Configure os detalhes da sua nova campanha de WhatsApp.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Campaign Name */}
          <div className="space-y-2">
            <Label htmlFor="campaign-name">Nome da Campanha *</Label>
            <Input
              id="campaign-name"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="Ex: Black Friday 2024"
            />
          </div>

          {/* Date Range */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="start-date">Data de Inicio *</Label>
              <Input
                id="start-date"
                type="text"
                inputMode="numeric"
                value={startDateDisplay}
                onChange={handleStartDateChange}
                placeholder="DD/MM/AAAA"
                maxLength={10}
              />
              {startDateDisplay && !isValidBrazilianDate(startDateDisplay) && startDateDisplay.length === 10 && (
                <p className="text-xs text-red-500">Data invalida</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">Data de Fim *</Label>
              <Input
                id="end-date"
                type="text"
                inputMode="numeric"
                value={endDateDisplay}
                onChange={handleEndDateChange}
                placeholder="DD/MM/AAAA"
                maxLength={10}
              />
              {endDateDisplay && !isValidBrazilianDate(endDateDisplay) && endDateDisplay.length === 10 && (
                <p className="text-xs text-red-500">Data invalida</p>
              )}
            </div>
          </div>

          {dateRange.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Duracao: {dateRange.length} dia{dateRange.length !== 1 ? 's' : ''}
            </p>
          )}

          {/* Run Time - 24h format */}
          <div className="space-y-2">
            <Label>Horario de Envio</Label>
            <div className="flex items-center gap-1">
              <select
                value={runTime.split(':')[0] || '09'}
                onChange={(e) => {
                  const minutes = runTime.split(':')[1] || '00';
                  setRunTime(`${e.target.value}:${minutes}`);
                }}
                className="flex h-9 w-16 rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map((hour) => (
                  <option key={hour} value={hour}>{hour}</option>
                ))}
              </select>
              <span className="text-lg font-medium">:</span>
              <select
                value={runTime.split(':')[1] || '00'}
                onChange={(e) => {
                  const hours = runTime.split(':')[0] || '09';
                  setRunTime(`${hours}:${e.target.value}`);
                }}
                className="flex h-9 w-16 rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {['00', '15', '30', '45'].map((minute) => (
                  <option key={minute} value={minute}>{minute}</option>
                ))}
              </select>
            </div>
            <p className="text-xs text-muted-foreground">
              Horario em que as mensagens serao enviadas
            </p>
          </div>

          {/* Template Mode */}
          <div className="space-y-2">
            <Label>Modo de Template *</Label>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="templateMode"
                  value="SAME_FOR_ALL"
                  checked={templateMode === 'SAME_FOR_ALL'}
                  onChange={() => setTemplateMode('SAME_FOR_ALL')}
                  className="h-4 w-4"
                />
                <div>
                  <div className="font-medium text-sm">Mesmo template para todos os dias</div>
                  <div className="text-xs text-muted-foreground">
                    Todos os contatos receberao o mesmo template durante a campanha.
                  </div>
                </div>
              </label>
              <label className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="templateMode"
                  value="DIFFERENT_PER_DAY"
                  checked={templateMode === 'DIFFERENT_PER_DAY'}
                  onChange={() => setTemplateMode('DIFFERENT_PER_DAY')}
                  className="h-4 w-4"
                />
                <div>
                  <div className="font-medium text-sm">Template diferente por dia</div>
                  <div className="text-xs text-muted-foreground">
                    Configure um template diferente para cada dia da campanha.
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Template Selection */}
          {templateMode === 'SAME_FOR_ALL' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="default-template">Template *</Label>
                <select
                  id="default-template"
                  value={defaultTemplateId}
                  onChange={(e) => handleDefaultTemplateChange(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  disabled={templatesLoading}
                >
                  <option value="">Selecione um template</option>
                  {approvedTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.language})
                    </option>
                  ))}
                </select>
                {approvedTemplates.length === 0 && !templatesLoading && (
                  <p className="text-xs text-muted-foreground">
                    Nenhum template aprovado disponivel. Crie e aguarde a aprovacao de templates na aba Templates.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Day-by-day Template Selection */}
          {templateMode === 'DIFFERENT_PER_DAY' && dateRange.length > 0 && (
            <div className="space-y-2">
              <Label>Templates por Dia *</Label>
              <div className="max-h-80 overflow-y-auto space-y-3 border rounded-lg p-3">
                {dateRange.map((date, index) => {
                  return (
                    <div key={index} className="space-y-2 pb-3 border-b last:border-b-0 last:pb-0">
                      <div className="flex items-center gap-3">
                        <div className="min-w-[100px] text-sm">
                          <div className="font-medium">Dia {index + 1}</div>
                          <div className="text-xs text-muted-foreground">
                            {format(date, 'dd/MM', { locale: ptBR })}
                          </div>
                        </div>
                        <select
                          value={dayTemplates[index] || ''}
                          onChange={(e) => handleDayTemplateChange(index, e.target.value)}
                          className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm"
                          disabled={templatesLoading}
                        >
                          <option value="">Selecione...</option>
                          {approvedTemplates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Contact Selection (Batches + Individual Contacts) */}
          <div className="space-y-4">
            <div>
              <Label>Contatos (opcional)</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Selecione lotes ou contatos individuais que participarao da campanha. Voce pode adicionar mais depois.
              </p>
            </div>

            {/* Batch Selection */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Layers className="h-4 w-4" />
                Lotes de Contatos
              </div>
              <SearchableMultiSelect
                options={batchOptions}
                selected={selectedBatchIds}
                onSelectionChange={(ids) => setSelectedBatchIds(ids as number[])}
                placeholder="Selecionar lotes..."
                searchPlaceholder="Buscar lotes..."
                emptyMessage="Nenhum lote encontrado."
                loading={batchesLoading}
                loadingMessage="Carregando lotes..."
              />
            </div>

            {/* Individual Contacts Selection */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Users className="h-4 w-4" />
                Contatos Individuais
              </div>
              <SearchableMultiSelect
                options={contactOptions}
                selected={selectedContactIds}
                onSelectionChange={(ids) => setSelectedContactIds(ids as number[])}
                placeholder="Selecionar contatos..."
                searchPlaceholder="Buscar por nome ou telefone..."
                emptyMessage="Nenhum contato encontrado."
                loading={contactsLoading}
                loadingMessage="Carregando contatos..."
              />
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={creating}
          >
            Cancelar
          </Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating ? 'Criando...' : 'Criar Campanha'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
